/**
 * <variant-picker> — listens to option-input changes, looks up the matching
 * variant in the data-variants JSON script tag, updates:
 *   - the URL ?variant=…
 *   - the price + compare-at + save-chip nodes (data-variant-price, data-variant-compare, data-variant-save)
 *   - availability (Add to bag → Sold out)
 *   - low-stock indicator
 *   - the gallery (dispatches "media:select" on the document with media id)
 */

class VariantPicker extends globalThis.ThemeElement {
  connectedCallback() {
    const dataNode = this.$('[data-variants-json]');
    try {
      this.variants = JSON.parse(dataNode?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.product = this.dataset.product;

    this.$$('input[type="radio"][data-option-position]').forEach((input) => {
      this.on(input, 'change', this.onOptionChange.bind(this));
    });
  }

  onOptionChange() {
    const selected = this.getSelectedOptionValues();
    const variant = this.findVariant(selected);
    if (!variant) return;
    this.update(variant);
  }

  getSelectedOptionValues() {
    const values = [];
    const inputs = this.$$('input[type="radio"][data-option-position]:checked');
    inputs.sort((a, b) => parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10));
    for (const input of inputs) values.push(input.value);
    return values;
  }

  findVariant(selectedValues) {
    return this.variants.find((v) => {
      return selectedValues.every((val, i) => v.options[i] === val);
    });
  }

  update(variant) {
    /* URL */
    if (variant.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    /* Update the form's hidden variant id input so add-to-cart submits the right variant.
       Scoped to the matching <product-form> by product handle so we don't cross-contaminate
       if multiple products are on the page (recommendations, related, etc.). */
    const formScope = document.querySelector(`[data-product-form="${this.product}"]`) || document;
    const variantInput = formScope.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = variant.id;

    /* Update each option's "— selected_value" label inside its <legend>. */
    const selectedValues = this.getSelectedOptionValues();
    this.$$('[data-option-fieldset]').forEach((fs) => {
      const pos = parseInt(fs.dataset.optionPosition, 10);
      const target = fs.querySelector('[data-option-selected-value]');
      if (target && selectedValues[pos - 1]) target.textContent = `— ${selectedValues[pos - 1]}`;
    });

    /* Price + compare + save chip nodes live in the buybox column outside the variant-picker
       element. Search the product-form scope (or document) instead of `this`. */
    const priceEl = formScope.querySelector('[data-variant-price]');
    const compareEl = formScope.querySelector('[data-variant-compare]');
    const saveEl = formScope.querySelector('[data-variant-save]');
    if (priceEl && variant.price_formatted) priceEl.textContent = variant.price_formatted;
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = variant.compare_at_price_formatted || '';
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (saveEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        saveEl.textContent = saveEl.dataset.saveTemplate?.replace('__PCT__', pct) || `Save ${pct}%`;
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }

    /* Availability */
    const submit = document.querySelector(`[data-product-form="${this.product}"] [data-add-to-cart]`);
    if (submit) {
      submit.disabled = !variant.available;
      submit.dataset.variantId = variant.id;
      const labelEl = submit.querySelector('[data-add-label]');
      if (labelEl) labelEl.textContent = variant.available ? labelEl.dataset.addLabel : labelEl.dataset.soldoutLabel;
    }

    /* Low stock */
    const lowEl = document.querySelector('[data-low-stock]');
    if (lowEl) {
      if (variant.available && variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity < 5) {
        lowEl.textContent = lowEl.dataset.template?.replace('__N__', variant.inventory_quantity) || `Only ${variant.inventory_quantity} left`;
        lowEl.hidden = false;
      } else {
        lowEl.hidden = true;
      }
    }

    /* Featured media → gallery */
    if (variant.featured_media_id) {
      document.dispatchEvent(new CustomEvent('media:select', { detail: { mediaId: variant.featured_media_id } }));
    }

    this.emit('variant:change', { variant });
  }
}

if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
